import React from 'react';
import { FaFileAlt, FaExclamationTriangle, FaCheckCircle, FaBalanceScale } from 'react-icons/fa';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Terms of Service</h1>
            <p className="text-xl md:text-2xl text-green-100 max-w-3xl mx-auto">
              Please read these terms carefully before using our platform
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-8">
              <strong>Last updated:</strong> {new Date().toLocaleDateString()}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FaFileAlt className="h-6 w-6 mr-2 text-green-600" />
                Acceptance of Terms
              </h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing and using the Jhonks platform, you accept and agree to be bound by the terms and provision of this agreement. 
                If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FaBalanceScale className="h-6 w-6 mr-2 text-green-600" />
                Description of Service
              </h2>
              <p className="text-gray-700 mb-4">
                Jhonks is a waste management platform that connects sellers of waste materials with verified agents for:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Material validation and assessment</li>
                <li>Collection and transportation services</li>
                <li>Fair pricing and payment processing</li>
                <li>Sustainable waste management solutions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FaCheckCircle className="h-6 w-6 mr-2 text-green-600" />
                User Responsibilities
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">For Sellers</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>Provide accurate descriptions of materials</li>
                <li>Ensure materials are properly categorized</li>
                <li>Maintain honest communication with agents</li>
                <li>Follow safety guidelines for material handling</li>
                <li>Comply with local environmental regulations</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">For Agents</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>Maintain professional certification and credentials</li>
                <li>Provide accurate material assessments</li>
                <li>Offer fair and competitive pricing</li>
                <li>Follow safety protocols during collection</li>
                <li>Maintain proper documentation of transactions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FaExclamationTriangle className="h-6 w-6 mr-2 text-green-600" />
                Prohibited Activities
              </h2>
              <p className="text-gray-700 mb-4">Users are prohibited from:</p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Posting false or misleading information</li>
                <li>Engaging in fraudulent activities</li>
                <li>Violating any applicable laws or regulations</li>
                <li>Attempting to hack or compromise the platform</li>
                <li>Spamming or harassing other users</li>
                <li>Listing prohibited or dangerous materials</li>
                <li>Circumventing platform fees or policies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Terms</h2>
              <ul className="list-disc pl-6 text-gray-700">
                <li>All transactions are processed through secure payment systems</li>
                <li>Platform fees are clearly disclosed before transactions</li>
                <li>Refunds are subject to our refund policy</li>
                <li>Users are responsible for any applicable taxes</li>
                <li>Payment disputes should be reported within 30 days</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                The Jhonks platform, including its design, functionality, and content, is protected by intellectual property laws. 
                Users may not:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Copy, modify, or distribute platform content without permission</li>
                <li>Use our trademarks or logos without authorization</li>
                <li>Reverse engineer or attempt to extract source code</li>
                <li>Create derivative works based on our platform</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                Jhonks shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Loss of profits, data, or business opportunities</li>
                <li>Damages resulting from material handling or transportation</li>
                <li>Third-party actions or content</li>
                <li>System downtime or technical issues</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Termination</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to suspend or terminate accounts that violate these terms. Users may also terminate their accounts at any time.
              </p>
              <p className="text-gray-700">
                Upon termination, users remain responsible for any outstanding obligations and must cease using the platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Dispute Resolution</h2>
              <p className="text-gray-700 mb-4">
                Any disputes arising from these terms or use of the platform will be resolved through:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Good faith negotiation between parties</li>
                <li>Mediation if negotiation fails</li>
                <li>Binding arbitration as a last resort</li>
                <li>Governing law: Laws of Nigeria</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
              <p className="text-gray-700">
                We reserve the right to modify these terms at any time. Users will be notified of significant changes, 
                and continued use of the platform constitutes acceptance of the updated terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For questions about these Terms of Service, please contact us:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700"><strong>Email:</strong> legal@jhonks.com</p>
                <p className="text-gray-700"><strong>Phone:</strong> +234 (0) 800 123 4567</p>
                <p className="text-gray-700"><strong>Address:</strong> 123 Innovation Street, Lagos, Nigeria</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
